/* eslint-disable  @typescript-eslint/no-explicit-any */
import React, {
    createContext,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react'
import { useAppStore, useEPGStore, useTVStore } from '@/shared/store'
import { useEPGCache } from '@/shared/cache/hooks/useEPGCache'
import {
    epgDataKey,
    IS_EPG_CACHE_CHECKED,
    IS_TV_CACHE_CHECKED,
    MILIS_24h,
    PRESENT_FOLLOWING_REFRESH_INTERVAL,
    tvDataKey,
} from '@/shared/constants'
import {
    bootstrapsKeyword,
    getFromLocalStorage,
    lastTimeStart,
    refreshTokenKeyword,
    removeFromLocalStorage,
    saveToLocalStorage,
    sessionTokenLsKeyword,
} from '@/shared/localStorage/localStorageRepository'
import { useSetTVAndEpgToStateAndCache } from '@/shared/hooks/useSetEpgToStateAndCache'
import { EpgObject } from '@/types/epg/Epg'
import { getErrorMessage } from '@/shared/lib'
import { useSetCategoryIfAllGenres } from '@/shared/hooks'
import { shallow } from 'zustand/shallow'
import { ChannelType } from '@/shared/types/appTypes'
import { getTvService } from '@/shared/getContainer/getTvService'
import { getEPGRepository } from '@/shared/getContainer/getEPGRepository'
import { getUseCategoriesForFilteringHook } from '@/shared/getContainer/getUseCategoriesForFilteringHook'
import { getConfigHook } from '@/shared/getContainer/getConfigHook'

const useCategoriesForFiltering = await getUseCategoriesForFilteringHook()
const TvService = await getTvService()
const useConfig = await getConfigHook()
const useEPGRepository = await getEPGRepository()

const ComponentsRenderContext = createContext<any>(null)

export const ComponentsRenderProvider = ({
    children,
}: {
    children: React.ReactNode
}) => {
    const [isComponentsReadyToRender, setIsComponentsReadyToRender] =
        useState(false)

    const { channelsData, setChannelsData, isStoreHydrated } = useTVStore(
        (state) => ({
            channelsData: state.channelsData,
            setChannelsData: state.setChannelsData,
            isStoreHydrated: state.isStoreHydrated,
        }),
        shallow
    )
    const { epgData, setEpg } = useEPGStore(
        (state) => ({
            epgData: state.epgData,
            setEpg: state.setEpg,
        }),
        shallow
    )
    const { actualProfile, isCacheChecked, setIsCacheChecked } = useAppStore()

    const { getEpgFromCache, getTvFromCache, clearCache } = useEPGCache()

    const { takeConfigIfNeed } = useConfig()

    const { createEPG, getProgramsForAllChannels } = useEPGRepository()

    const { getCategoriesForFiltering } = useCategoriesForFiltering()

    useSetCategoryIfAllGenres()

    const { setEpgToStoreAndCache, setTvToStoreAndCache } =
        useSetTVAndEpgToStateAndCache()

    const checkLastLoginDataANdCleanCacheIfNeed = async () => {
        const lastTime = getFromLocalStorage(lastTimeStart)
        const dateNow = Date.now()

        if (lastTime && dateNow - Number(lastTime) > MILIS_24h) {
            clearCache()
            removeFromLocalStorage(sessionTokenLsKeyword)
            removeFromLocalStorage(bootstrapsKeyword)
            removeFromLocalStorage(refreshTokenKeyword)
            removeFromLocalStorage(IS_EPG_CACHE_CHECKED)
            removeFromLocalStorage(IS_TV_CACHE_CHECKED)
            saveToLocalStorage(lastTimeStart, `${dateNow}`)
        } else if (!lastTime) {
            saveToLocalStorage(lastTimeStart, `${dateNow}`)
        }
    }

    async function fetchChannels() {
        try {
            const channelsList: ChannelType[] | undefined =
                await TvService.getApiChannelsList()

            if (channelsList) {
                const sortedChannels = channelsList.sort(
                    (channelA, channelB) =>
                        Number(channelA.lcn) - Number(channelB.lcn)
                )
                setTvToStoreAndCache(sortedChannels)
            }

            return channelsList
        } catch (error) {
            getErrorMessage('Error fetching channels:', error)
        }
    }

    const checkAndClearCacheIfNeeded = async () => {
        await checkLastLoginDataANdCleanCacheIfNeed()
    }

    const initializeCacheAndTv = async () => {
        try {
            const cachedTV = await getTvFromCache()
            const res = await fetchChannels()

            if (res) {
                return
            } else if (cachedTV?.length) {
                setChannelsData(cachedTV)
            }
        } catch (error) {
            getErrorMessage('Error during cache or fetch initialization', error)
        } finally {
            setIsCacheChecked(true)
            saveToLocalStorage(IS_TV_CACHE_CHECKED, 'true')
        }
    }

    const initializeCacheAndEpg = async () => {
        try {
            const cachedEpg = await getEpgFromCache()
            const isEpgCacheChecked = JSON.parse(
                getFromLocalStorage(IS_EPG_CACHE_CHECKED) || ''
            )

            const programs = await getProgramsForAllChannels()

            if (programs?.length) {
                await setupEpgDataFromChannels(channelsData, programs)
            } else if (cachedEpg?.length && isEpgCacheChecked) {
                setEpg(cachedEpg)
            } else {
                console.log('No cache and no programs found')
            }
        } catch (error) {
            console.error('Error during cache or fetch initialization', error)
        } finally {
            setIsCacheChecked(true)
            saveToLocalStorage(IS_EPG_CACHE_CHECKED, 'true')
        }
    }

    const initializeChannelsAndEpgData = async () => {
        if (!channelsData.length) {
            await initializeCacheAndTv()
        } else if (channelsData.length && isStoreHydrated) {
            await initializeCacheAndEpg()
        }
    }

    const getPresentFollowing = async () => {
        const programs = await getProgramsForAllChannels()
        if (!programs?.length) return
        await setupEpgDataFromChannels(channelsData, programs)
    }

    const setupEpgDataFromChannels = async (
        channelsList: ChannelType[],
        programs: EpgObject[]
    ) => {
        setEpgToStoreAndCache([])
        const fullEPG = await createEPG(channelsList, programs)
        if (fullEPG) {
            setEpgToStoreAndCache(fullEPG)
        }
    }

    useEffect(() => {
        if (!isStoreHydrated) return

        async function createAndSetChannels() {
            await checkAndClearCacheIfNeeded()
            await initializeChannelsAndEpgData()
            await takeConfigIfNeed()
        }

        createAndSetChannels()
    }, [
        isStoreHydrated,
        channelsData.length,
        isCacheChecked,
        isStoreHydrated,
        actualProfile?.id,
    ])

    useEffect(() => {
        setIsCacheChecked(false)
        saveToLocalStorage(IS_EPG_CACHE_CHECKED, 'false')
        saveToLocalStorage(IS_TV_CACHE_CHECKED, 'false')
        removeFromLocalStorage(tvDataKey)
        removeFromLocalStorage(epgDataKey)
    }, [actualProfile?.id])

    useEffect(() => {
        // Refresh present-following in TV

        const intervalId = setInterval(() => {
            getPresentFollowing()
        }, PRESENT_FOLLOWING_REFRESH_INTERVAL)

        return () => clearInterval(intervalId)
    }, [])

    useEffect(() => {
        if (!channelsData.length) return

        setIsComponentsReadyToRender(true)
        getCategoriesForFiltering()
        if (!epgData.length) initializeCacheAndEpg()
    }, [channelsData.length, epgData.length])

    return (
        <ComponentsRenderContext.Provider
            value={useMemo(
                () => ({
                    isComponentsReadyToRender,
                    getCategoriesForFiltering,
                }),
                [isComponentsReadyToRender]
            )}>
            {children}
        </ComponentsRenderContext.Provider>
    )
}

export const useComponentsRender = () => useContext(ComponentsRenderContext)
